import { useCallback, useEffect, useRef, useState } from 'react';

const ACTIVITY_EVENTS = ['mousemove', 'keydown', 'click', 'scroll', 'touchstart'];

/**
 * Detects user inactivity and fires callbacks at configurable thresholds.
 *
 * @param {object} options
 * @param {number}   options.timeout       Total idle time (ms) before onExpire fires.
 * @param {number}   options.warningBefore How many ms before timeout to fire onWarn.
 * @param {Function} [options.onWarn]      Called when warningBefore ms remain.
 * @param {Function} [options.onExpire]    Called when timeout is reached.
 * @param {boolean}  [options.enabled]     Disable the hook entirely (default: true).
 *
 * Listens for DOM events + a custom 'api-activity' window event dispatched by
 * the Axios request interceptor, so API calls also reset the idle timer.
 *
 * @returns {{ isWarning: boolean, reset: Function }}
 */
export function useIdleTimeout({
  timeout,
  warningBefore,
  onWarn,
  onExpire,
  enabled = true,
}) {
  const [isWarning, setIsWarning] = useState(false);

  const timeoutRef  = useRef(null);
  const warningRef  = useRef(null);
  // Stable refs so callbacks don't cause the effect to re-run on every render
  const onWarnRef   = useRef(onWarn);
  const onExpireRef = useRef(onExpire);

  useEffect(() => { onWarnRef.current   = onWarn;   });
  useEffect(() => { onExpireRef.current = onExpire; });

  const reset = useCallback(() => {
    setIsWarning(false);
    clearTimeout(timeoutRef.current);
    clearTimeout(warningRef.current);

    warningRef.current = setTimeout(() => {
      setIsWarning(true);
      onWarnRef.current?.();
    }, timeout - warningBefore);

    timeoutRef.current = setTimeout(() => {
      onExpireRef.current?.();
    }, timeout);
  }, [timeout, warningBefore]);

  useEffect(() => {
    if (!enabled) {
      clearTimeout(timeoutRef.current);
      clearTimeout(warningRef.current);
      return;
    }

    ACTIVITY_EVENTS.forEach(e =>
      window.addEventListener(e, reset, { passive: true })
    );
    window.addEventListener('api-activity', reset);
    reset();

    return () => {
      ACTIVITY_EVENTS.forEach(e => window.removeEventListener(e, reset));
      window.removeEventListener('api-activity', reset);
      clearTimeout(timeoutRef.current);
      clearTimeout(warningRef.current);
    };
  }, [enabled, reset]);

  return { isWarning, reset };
}
